import { ChangeDetectionStrategy, Component, computed, inject, input, signal, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { PlatformWidgetComponent } from '../platform-widget.component';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom, map, Observable, of, Subject, switchMap } from 'rxjs';
import { BookingService, CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { Cartridge, CartridgeVariant } from '@markant-platform/cartridge/model';
import { MarkantElementsModule } from '@markant/elements-angular';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { RouterLink } from '@angular/router';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { SharedDialogFeatureModule } from '@markant-platform/shared/dialog/feature';
import { SimpleWidgetWithActionComponent } from '../simple-widget-with-action/simple-widget-with-action.component';

type UserType = 'SERVICE_MANAGER' | 'SERVICE_MANAGER_ECS' | 'NON_SERVICE_MANAGER';

@Component({
  selector: 'markant-platform-blocked-service-widget',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoPipe,
    MarkantElementsModule,
    RouterLink,
    ModalBridgeComponent,
    SharedDialogFeatureModule,
    SimpleWidgetWithActionComponent,
  ],
  templateUrl: './blocked-service-widget.component.html',
  styleUrls: ['./blocked-service-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockedServiceWidgetComponent extends PlatformWidgetComponent {
  cartridgesFacade = inject(CartridgesFacade);
  authenticatedUserService = inject(AuthenticatedUserService);
  organizationService = inject(OrganizationService);
  bookingService = inject(BookingService);

  cartridgeId = input.required<string>();
  serviceVariant = toSignal<CartridgeVariant | undefined>(
    toObservable(this.cartridgeId).pipe(switchMap(id => this.cartridgesFacade.cartridgeVariantById$(id)))
  );
  service = toSignal<Cartridge | undefined>(
    toObservable(this.cartridgeId).pipe(switchMap(id => this.cartridgesFacade.cartridgeByVariantId$(id)))
  );
  userType: Signal<UserType | undefined> = toSignal<UserType>(
    toObservable(this.service).pipe(
      map(service => service?.serviceManagerId ?? ''),
      switchMap<string, Observable<UserType>>(id => this.setUserTypeByServiceManagerId$(id))
    )
  );
  serviceManagerName = computed(() => this.service()?.serviceManagerName);
  contactRequestDialogOpen = signal(false);
  requestSucceeded$ = new Subject<boolean>();

  async sendContactRequest(): Promise<void> {
    try {
      await firstValueFrom(
        this.bookingService.requestNegotiation({ cartridgeVariantId: this.cartridgeId(), type: 'CONTACT_REQUEST' })
      );
      this.requestSucceeded$.next(true);
    } catch {
      this.requestSucceeded$.next(false);
    }
  }

  private setUserTypeByServiceManagerId$(id: string): Observable<UserType> {
    return id === this.authenticatedUserService.userId
      ? this.organizationService
          .getCurrentOrganization()
          .pipe(map(org => (org?.ezrCustomer ? 'SERVICE_MANAGER_ECS' : 'SERVICE_MANAGER')))
      : of('NON_SERVICE_MANAGER');
  }
}
